import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader, PageBody } from "@/components/app/page-header";
import { MetricCard, SectionCard, StatusBadge, RiskBadge, PriorityBadge } from "@/components/app/ui-bits";
import { Button } from "@/components/ui/button";
import {
  Briefcase,
  FileSearch,
  Calendar as CalendarIcon,
  FolderOpen,
  Sparkles,
  ArrowRight,
  Plus,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/dashboard")({
  component: DashboardPage,
});

type Counts = {
  matters: number;
  open: number;
  analyses: number;
  events_week: number;
  documents: number;
};
type Matter = { id: string; name: string; status: string | null; updated_at: string };
type Analysis = { id: string; library_doc_id: string | null; risk_level: string | null; created_at: string; summary: string | null };
type EventRow = { id: string; title: string; starts_at: string; priority: string | null; type: string | null };

function DashboardPage() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.resolvedLanguage?.startsWith("en") ? "en-US" : "it-IT";
  const [firstName, setFirstName] = useState("");
  const [counts, setCounts] = useState<Counts>({ matters: 0, open: 0, analyses: 0, events_week: 0, documents: 0 });
  const [matters, setMatters] = useState<Matter[]>([]);
  const [analyses, setAnalyses] = useState<Analysis[]>([]);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [aiUsed, setAiUsed] = useState(0);
  const [aiLimit, setAiLimit] = useState(0);

  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return;
      const uid = u.user.id;
      const { data: profile } = await supabase
        .from("profiles")
        .select("first_name, ai_queries_used, ai_queries_limit")
        .eq("id", uid)
        .maybeSingle();
      setFirstName(profile?.first_name ?? "");
      setAiUsed(profile?.ai_queries_used ?? 0);
      setAiLimit(profile?.ai_queries_limit ?? 0);

      const weekAhead = new Date(Date.now() + 7 * 86400000).toISOString();
      const now = new Date().toISOString();

      const [m, a, e, d] = await Promise.all([
        supabase.from("matters").select("id,name,status,updated_at").order("updated_at", { ascending: false }).limit(5),
        supabase.from("document_analyses").select("id,library_doc_id,risk_level,created_at,summary").order("created_at", { ascending: false }).limit(5),
        supabase.from("calendar_events").select("id,title,starts_at,priority,type").gte("starts_at", now).lte("starts_at", weekAhead).order("starts_at").limit(5),
        supabase.from("library_documents").select("id", { count: "exact", head: true }),
      ]);

      const allMatters = await supabase.from("matters").select("status", { count: "exact" });
      const open = (allMatters.data ?? []).filter((x: any) => (x.status ?? "open") !== "closed" && x.status !== "done").length;

      setMatters((m.data ?? []) as Matter[]);
      setAnalyses((a.data ?? []) as Analysis[]);
      setEvents((e.data ?? []) as EventRow[]);
      setCounts({
        matters: allMatters.count ?? 0,
        open,
        analyses: a.data?.length ?? 0,
        events_week: e.data?.length ?? 0,
        documents: d.count ?? 0,
      });
    })();
  }, []);

  return (
    <>
      <PageHeader
        title={firstName ? t("pages.dashboard.greeting", { name: firstName }) : t("pages.dashboard.greetingDefault")}
        description={t("pages.dashboard.description")}
        actions={
          <div className="flex items-center gap-2">
            <span className="hidden items-center gap-1.5 rounded-full border border-border bg-secondary px-3 py-1 text-[11px] text-foreground sm:inline-flex">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--lex-success)] shadow-[0_0_8px_rgba(52,211,153,0.7)]" />
              {t("pages.dashboard.allGood")}
            </span>
            <Link to="/app/matters">
              <Button variant="outline" className="gap-2 border-border bg-secondary text-foreground hover:bg-secondary"><Plus className="h-4 w-4" /> {t("pages.dashboard.newMatter")}</Button>
            </Link>
            <Link to="/app/analysis">
              <Button className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90"><Sparkles className="h-4 w-4" /> {t("pages.dashboard.newAnalysis")}</Button>
            </Link>
          </div>
        }
      />
      <PageBody>
        <div className="mb-2 lex-eyebrow">{t("pages.dashboard.overview")}</div>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <MetricCard label={t("pages.dashboard.openMatters")} value={counts.open} hint={t("pages.dashboard.mattersTotal", { n: counts.matters })} />
          <MetricCard label={t("pages.dashboard.deadlines7d")} value={counts.events_week} />
          <MetricCard label={t("pages.dashboard.researchToday")} value={counts.analyses} />
          <MetricCard label={t("pages.dashboard.documents")} value={counts.documents} />
          <MetricCard label={t("pages.dashboard.aiQueries")} value={`${aiUsed}/${aiLimit || "∞"}`} hint={t("pages.dashboard.currentPeriod")} />
        </div>

        <div className="mt-6 grid gap-4 lg:grid-cols-3">
          <SectionCard
            title={t("pages.dashboard.recentMatters")}
            eyebrow={t("pages.dashboard.studio")}
            action={<Link to="/app/matters" className="text-xs text-[var(--lex-muted-fg)] hover:text-[var(--lex-fg)]">{t("pages.dashboard.seeAll")}</Link>}
          >
            {matters.length === 0 ? (
              <Empty icon={<Briefcase className="h-6 w-6" />} label={t("pages.dashboard.noMatters")} cta={{ to: "/app/matters", label: t("pages.dashboard.createMatter") }} />
            ) : (
              <ul className="divide-y divide-[var(--lex-border)]">
                {matters.map((m) => (
                  <li key={m.id} className="flex items-center justify-between gap-3 py-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{m.name}</div>
                      <div className="text-xs text-[var(--lex-muted-fg)]">
                        {t("pages.dashboard.updated", { date: new Date(m.updated_at).toLocaleDateString(dateLocale) })}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={m.status} />
                      <Link to="/app/matters/$matterId" params={{ matterId: m.id }} className="text-[var(--lex-muted-fg)] hover:text-[var(--lex-fg)]">
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>

          <SectionCard
            title={t("pages.dashboard.recentAnalyses")}
            eyebrow={t("pages.dashboard.docs")}
            action={<Link to="/app/analyses" className="text-xs text-[var(--lex-muted-fg)] hover:text-[var(--lex-fg)]">{t("pages.dashboard.historyArrow")}</Link>}
          >
            {analyses.length === 0 ? (
              <Empty icon={<FileSearch className="h-6 w-6" />} label={t("pages.dashboard.noAnalyses")} cta={{ to: "/app/analysis", label: t("pages.dashboard.analyzeDoc") }} />
            ) : (
              <ul className="space-y-3">
                {analyses.map((a) => (
                  <li key={a.id} className="rounded-lg border border-[var(--lex-border)] p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs text-[var(--lex-muted-fg)]">
                        {new Date(a.created_at).toLocaleDateString(dateLocale)}
                      </div>
                      <RiskBadge level={(a.risk_level as any) ?? "low"} />
                    </div>
                    <div className="mt-1 line-clamp-2 text-sm">{a.summary ?? t("pages.dashboard.analysisDone")}</div>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>

          <SectionCard
            title={t("pages.dashboard.next7days")}
            eyebrow={t("pages.dashboard.calendar")}
            action={<Link to="/app/calendar" className="text-xs text-[var(--lex-muted-fg)] hover:text-[var(--lex-fg)]">{t("pages.dashboard.openArrow")}</Link>}
          >
            {events.length === 0 ? (
              <Empty icon={<CalendarIcon className="h-6 w-6" />} label={t("pages.dashboard.noDeadlines")} cta={{ to: "/app/calendar", label: t("pages.dashboard.createEvent") }} />
            ) : (
              <ul className="space-y-2">
                {events.map((e) => (
                  <li key={e.id} className="flex items-center gap-3 rounded-lg border border-[var(--lex-border)] p-3">
                    <div className="flex h-10 w-10 flex-col items-center justify-center rounded-md bg-[var(--lex-primary)] text-[10px] font-semibold leading-tight text-foreground">
                      <span className="uppercase">
                        {new Date(e.starts_at).toLocaleDateString(dateLocale, { month: "short" })}
                      </span>
                      <span className="text-base">{new Date(e.starts_at).getDate()}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{e.title}</div>
                      <div className="text-xs text-[var(--lex-muted-fg)]">
                        {new Date(e.starts_at).toLocaleTimeString(dateLocale, { hour: "2-digit", minute: "2-digit" })}
                        {e.type ? ` · ${e.type}` : ""}
                      </div>
                    </div>
                    <PriorityBadge priority={e.priority ?? "medium"} />
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <QuickAction to="/app/library" icon={<FolderOpen className="h-5 w-5" />} title={t("pages.dashboard.qaLibrary")} desc={t("pages.dashboard.qaLibraryDesc")} />
          <QuickAction to="/app/analysis" icon={<Sparkles className="h-5 w-5" />} title={t("pages.dashboard.qaAnalysis")} desc={t("pages.dashboard.qaAnalysisDesc")} />
          <QuickAction to="/app/generator" icon={<FileSearch className="h-5 w-5" />} title={t("pages.dashboard.qaGenerator")} desc={t("pages.dashboard.qaGeneratorDesc")} />
          <QuickAction to="/app/research" icon={<Briefcase className="h-5 w-5" />} title={t("pages.dashboard.qaResearch")} desc={t("pages.dashboard.qaResearchDesc")} />
        </div>
      </PageBody>
    </>
  );
}

function Empty({ icon, label, cta }: { icon: React.ReactNode; label: string; cta?: { to: string; label: string } }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-[var(--lex-border)] py-8 text-center text-sm text-[var(--lex-muted-fg)]">
      {icon}
      <div>{label}</div>
      {cta && (
        <Link to={cta.to as any} className="text-xs text-[var(--lex-fg)] underline-offset-2 hover:underline">
          {cta.label} →
        </Link>
      )}
    </div>
  );
}

function QuickAction({ to, icon, title, desc }: { to: string; icon: React.ReactNode; title: string; desc: string }) {
  return (
    <Link to={to as any} className="lex-card flex items-start gap-3 p-4 transition hover:shadow-md">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--lex-primary)] text-foreground">{icon}</div>
      <div>
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-xs text-[var(--lex-muted-fg)]">{desc}</div>
      </div>
    </Link>
  );
}
